import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QueryHookOptions } from '@apollo/react-hooks';

import {
	GetAccountInfoQuery,
	GetUserSettingsQuery,
	GetUserSettingsQueryVariables,
	LoginType,
	NewTravellerParameters,
	useAccountExistenceQuery,
	useGetAccountInfoQuery,
	useGetUserSettingsQuery,
	UserFragment,
	useSendPersonalDetailsMutation
} from '@websky/graphql';
import { UserSettings } from './types';
import { AuthDuplicates } from '../types';
import { getUserSettingsByName, mergeUserData } from './utils';
import { AUTH_DUPLICATES, removeFromSessionStorage, sessionStorageGet } from '../cache';
import { Log } from '../sentry';

export const useUserSettings = (options?: QueryHookOptions<GetUserSettingsQuery, GetUserSettingsQueryVariables>) => {
	const { data, loading, refetch } = useGetUserSettingsQuery(options);

	return {
		userSettings: {
			travellerId: getUserSettingsByName(data?.UserSettings, UserSettings.TravellerID),
			termOfUse: getUserSettingsByName(data?.UserSettings, UserSettings.TermOfUse),
			subscribed: getUserSettingsByName(data?.UserSettings, UserSettings.Subscribe) === 'true'
		},
		refetchUserSettings: refetch,
		loading
	};
};

export const useEmailDuplication = (accountInfo?: GetAccountInfoQuery['CurrentUser']) => {
	const emailIsChecked = useRef(false);
	const [isLoading, setIsLoading] = useState(false);

	const { refetch: checkAccountExistence } = useAccountExistenceQuery({ skip: true });

	const authDuplicates = sessionStorageGet<AuthDuplicates>(AUTH_DUPLICATES);

	const { emailMethod, ffpMethod, ffpEmail } = useMemo(() => {
		const authMethods = accountInfo?.authMethods ?? [];

		return {
			emailMethod: authMethods.find(method => method.loginType === LoginType.Email),
			ffpMethod: authMethods.find(method => method.loginType === LoginType.FFP),
			ffpEmail: accountInfo?.userFfpInfo?.registerEmail ?? null
		};
	}, [accountInfo]);

	const isNeedConnectFfp = useMemo(() => {
		if (!authDuplicates || !authDuplicates.isExistDuplicate || isLoading) {
			return false;
		}

		return !ffpMethod || (ffpMethod && !ffpMethod.confirmed);
	}, [ffpMethod, authDuplicates, isLoading]);

	const isNeedConnectEmail = useMemo(() => {
		if (!authDuplicates || authDuplicates.isExistDuplicate || isLoading) {
			return false;
		}

		return !emailMethod || (emailMethod && !emailMethod.confirmed);
	}, [emailMethod, authDuplicates, isLoading]);

	const connectFfp = isNeedConnectFfp ? authDuplicates?.login : null;

	const connectEmail = isNeedConnectEmail ? ffpEmail : null;

	const onCheckEmailExistence = useCallback(
		async (email: string) => {
			setIsLoading(true);

			const { data } = await checkAccountExistence({
				params: {
					loginType: LoginType.Email,
					login: email
				}
			});

			if (data?.AccountExistence?.result) {
				removeFromSessionStorage(AUTH_DUPLICATES);
			}

			emailIsChecked.current = true;
			setIsLoading(false);
		},
		[emailIsChecked, checkAccountExistence]
	);

	const onConnectResult = useCallback(
		(result: boolean) => {
			const method = isNeedConnectFfp ? 'fpp card' : 'email';

			if (result) {
				Log({ level: 'info', message: `successful linking the ${method}` });
			} else {
				Log({ level: 'error', message: `unsuccessful linking the ${method}` });
			}

			removeFromSessionStorage(AUTH_DUPLICATES);
		},
		[isNeedConnectFfp]
	);

	useEffect(() => {
		if (authDuplicates && emailMethod?.confirmed && ffpMethod?.confirmed) {
			removeFromSessionStorage(AUTH_DUPLICATES);
		}

		// Before opening the form to bind the email to the account, we send a request
		// to check if the email exists. If it is already used in another account,
		// we do not open the modal window (clear session storage).
		if (!emailIsChecked.current && !authDuplicates?.isExistDuplicate && isNeedConnectEmail && connectEmail) {
			onCheckEmailExistence(connectEmail);
		}
	}, [authDuplicates]);

	return {
		// data
		authDuplicates,
		isLoading,
		isNeedConnectFfp,
		isNeedConnectEmail,
		connectFfp,
		connectEmail,

		// methods
		onConnectResult
	};
};

export const useProfileUpdateService = (user: UserFragment) => {
	const { data: accountInfo } = useGetAccountInfoQuery({
		fetchPolicy: 'no-cache',
		skip: !!user
	});

	const [updatePersonalDetails] = useSendPersonalDetailsMutation();

	return async (newFields: Partial<NewTravellerParameters>) => {
		const fieldsToApi = mergeUserData(user || accountInfo.CurrentUser.userProfile, newFields);

		return await updatePersonalDetails({
			variables: {
				params: fieldsToApi
			}
		});
	};
};
